import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  getNotifications,
  getNotificationUnreadCount,
  markNotificationRead as apiMarkNotificationRead,
  markAllNotificationsRead as apiMarkAllNotificationsRead,
} from "../api";
import { showToast } from "../utils/toast";

const NotificationContext = createContext(null);

const RECONNECT_INTERVALS = [1000, 2000, 5000, 10000, 30000];

// Helper to obtain current active token from canonical storage keys
const getStoredToken = () =>
  localStorage.getItem("ACCESS_TOKEN") ||
  localStorage.getItem("access") ||
  localStorage.getItem("accessToken") ||
  "";

// Resolve WebSocket URL dynamically without logging sensitive tokens
const buildWebSocketUrl = (token) => {
  const customWsUrl = process.env.REACT_APP_NOTIFICATION_WS_URL;
  if (customWsUrl) {
    const base = customWsUrl.replace(/\/$/, "");
    return `${base}/ws/notifications/?token=${encodeURIComponent(token)}`;
  }

  const isHttps = window.location.protocol === "https:";
  const protocol = isHttps ? "wss:" : "ws:";
  // In production HTTPS, use current host. In local HTTP dev, target ASGI port 8001.
  const host = isHttps ? window.location.host : `${window.location.hostname}:8001`;
  return `${protocol}//${host}/ws/notifications/?token=${encodeURIComponent(token)}`;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connectionState, setConnectionState] = useState("disconnected");

  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const isExplicitLogoutRef = useRef(false);
  const hasAuthFailureRef = useRef(false);

  // Synchronize initial state from REST endpoints
  const fetchInitialData = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      setLoading(true);
      const [listRes, countRes] = await Promise.allSettled([
        getNotifications(),
        getNotificationUnreadCount(),
      ]);

      if (listRes.status === "fulfilled") {
        const rawData = listRes.value?.data || listRes.value;
        const fetchedList = Array.isArray(rawData?.results)
          ? rawData.results
          : Array.isArray(rawData)
          ? rawData
          : [];

        setNotifications((prev) => {
          // Merge REST list with any realtime items received during fetch
          const map = new Map();
          // Add newly fetched items
          fetchedList.forEach((item) => {
            if (item && item.id != null) map.set(item.id, item);
          });
          // Preserve any existing realtime items not yet in REST response
          prev.forEach((item) => {
            if (item && item.id != null && !map.has(item.id)) {
              map.set(item.id, item);
            }
          });

          return Array.from(map.values()).sort(
            (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
          );
        });
      }

      if (countRes.status === "fulfilled") {
        const countData = countRes.value?.data || countRes.value;
        const count = typeof countData?.unread_count === "number"
          ? countData.unread_count
          : typeof countData === "number"
          ? countData
          : 0;
        setUnreadCount(count);
      }
    } catch (err) {
      // Graceful degradation: log warning and keep app usable
      console.warn("[Notifications] Failed to fetch notification state:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Establish authenticated WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (isExplicitLogoutRef.current || hasAuthFailureRef.current) return;

    const token = getStoredToken();
    if (!token) {
      setConnectionState("disconnected");
      return;
    }

    // Clean up existing connection before reconnecting
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }

    setConnectionState("connecting");
    const wsUrl = buildWebSocketUrl(token);

    try {
      const socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        setConnectionState("connected");
        reconnectAttemptsRef.current = 0;
        hasAuthFailureRef.current = false;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data && data.type === "notification.created" && data.notification) {
            const notif = data.notification;
            if (notif.id == null) return;

            setNotifications((prev) => {
              // Deduplicate by UserNotification.id
              if (prev.some((item) => item.id === notif.id)) {
                return prev;
              }

              // Show one realtime toast for genuinely new event
              const toastMsg = notif.title
                ? `${notif.title}: ${notif.body || ""}`
                : notif.body || "New Notification";
              showToast(toastMsg, "info");

              // Increment unread count if unread
              if (!notif.is_read) {
                setUnreadCount((c) => c + 1);
              }

              return [notif, ...prev];
            });
          }
        } catch (err) {
          console.warn("[Notifications] Failed to parse WebSocket message:", err);
        }
      };

      socket.onclose = (event) => {
        setConnectionState("disconnected");
        wsRef.current = null;

        // Code 4401 indicates expired or invalid token -> stop reconnect loop until re-auth
        if (event.code === 4401) {
          hasAuthFailureRef.current = true;
          return;
        }

        // Schedule reconnect with exponential backoff if not intentional logout
        if (!isExplicitLogoutRef.current && !hasAuthFailureRef.current) {
          const attempt = reconnectAttemptsRef.current;
          const delay =
            RECONNECT_INTERVALS[Math.min(attempt, RECONNECT_INTERVALS.length - 1)];
          reconnectAttemptsRef.current += 1;

          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            connectWebSocket();
            // Refetch REST state on reconnect recovery to catch missed events
            fetchInitialData();
          }, delay);
        }
      };

      socket.onerror = () => {
        // Handled via onclose lifecycle
      };
    } catch (err) {
      setConnectionState("disconnected");
    }
  }, [fetchInitialData]);

  // Mark single user notification as read
  const markRead = useCallback(async (userNotificationId) => {
    if (!userNotificationId) return;

    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((item) => {
        if (item.id === userNotificationId) {
          if (!item.is_read) {
            setUnreadCount((c) => Math.max(0, c - 1));
          }
          return { ...item, is_read: true, read_at: new Date().toISOString() };
        }
        return item;
      })
    );

    try {
      await apiMarkNotificationRead(userNotificationId);
    } catch (err) {
      console.warn("[Notifications] Mark read API error:", err);
    }
  }, []);

  // Mark all user notifications as read
  const markAllRead = useCallback(async () => {
    setNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        is_read: true,
        read_at: item.read_at || new Date().toISOString(),
      }))
    );
    setUnreadCount(0);

    try {
      await apiMarkAllNotificationsRead();
    } catch (err) {
      console.warn("[Notifications] Mark all read API error:", err);
    }
  }, []);

  // Watch authentication state changes & storage events (logout/login)
  useEffect(() => {
    fetchInitialData();
    connectWebSocket();

    const handleStorageChange = (e) => {
      if (
        e.key === "ACCESS_TOKEN" ||
        e.key === "access" ||
        e.key === "__LOGGING_OUT__"
      ) {
        if (!getStoredToken() || localStorage.getItem("__LOGGING_OUT__") === "1") {
          isExplicitLogoutRef.current = true;
          if (wsRef.current) {
            try {
              wsRef.current.close();
            } catch {}
            wsRef.current = null;
          }
          setNotifications([]);
          setUnreadCount(0);
          setConnectionState("disconnected");
        } else {
          isExplicitLogoutRef.current = false;
          hasAuthFailureRef.current = false;
          fetchInitialData();
          connectWebSocket();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {}
        wsRef.current = null;
      }
    };
  }, [fetchInitialData, connectWebSocket]);

  const value = {
    notifications,
    unreadCount,
    loading,
    connectionState,
    markRead,
    markAllRead,
    refreshNotifications: fetchInitialData,
    refreshUnreadCount: fetchInitialData,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
